/* eslint-disable @typescript-eslint/ban-ts-comment */
'use client';
import type { MenuProps } from 'antd';
import { Divider, Drawer, Menu, Modal, Skeleton } from 'antd';
import SkeletonAvatar from 'antd/es/skeleton/Avatar';
import clsx from 'clsx';
import { getCookie } from 'cookies-next';
import i18next from 'i18next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';

import { profile } from '@/lib/apis';
import { formatNumber } from '@/lib/helper';
import { getParameterByName } from '@/lib/utils';

import ButtonCore from '@/components/core/Button';
import Link from '@/components/core/Link';
import { openGameProvider } from '@/components/core/OpenGame';
import TruncatedText from '@/components/core/TruncatedText';
import useChangeLanguage from '@/components/hooks/useChangeLanguage';
import useClientTranslation from '@/components/hooks/useClientTranslation';
import useRenderNotiUser from '@/components/hooks/useRenderNotiUser';
import SwitchLanguage from '@/components/modules/Layouts/DesktopLayout/Header/components/SwitchLanguage';

import { gameMenuMobile } from '@/constant/gameMenu';

const Marquee = dynamic(() => import('react-fast-marquee'), {
  ssr: false,
  loading: () => {
    return (
      <span className=' max-h-[24px] w-full max-w-full items-center overflow-hidden text-clip pl-1 pt-1'>
        {`${i18next.t('topSlider.marqueeContent', {
          ns: 'HomePage',
        })}`}
      </span>
    );
  },
} as any);

const UserKycStatus = dynamic(
  () => import('@/app/[lng]/user/kyc/components/UserKycStatus')
);
const MobileHeader: React.FC = () => {
  const { t, lng } = useClientTranslation('HomePage');
  const [open, setOpen] = useState(false);
  const [isShowNoti, setIsShowNoti] = useState(false);
  const [isHomeScreenOpen, setIsHomeScreenOpen] = useState(false);
  const handleChangeLanguage = useChangeLanguage(lng as string);
  const token = getCookie('token');
  const { renderIcon, renderNotification, hasNotiUnRead, handleReadAll } =
    useRenderNotiUser() || {};
  const cate = getParameterByName('slug');
  const { isLoading, data: userProfile } = useSWR('/profile', () => profile(), {
    revalidateOnFocus: false,
  });

  const [activeGame, setActiveGame] = useState(cate);

  useEffect(() => {
    if (cate) setActiveGame(cate);
  }, [cate]);

  const closeDrawer = () => {
    setOpen(false);
    setIsShowNoti(false);
  };

  const onLogout = () => {
    window.popup?.logout(() => {
      closeDrawer();
    });
  };

  const onMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'thai' && lng === 'th') {
      return;
    }
    if (e.key === 'eng' && lng === 'en') {
      return;
    }
    if (e.key === 'thai' || e.key === 'eng') {
      handleChangeLanguage();
    }
  };
  const renderUserProfile = (className = '') => {
    if (userProfile) {
      return (
        <div className='vw:max-h-[60px] ml-auto flex items-center'>
          <div className='mr-3 flex flex-col items-end'>
            <div className='vw:text-14 font-bold opacity-[88%]'>
              <TruncatedText
                text={userProfile?.username as string}
                truncateOptions={[
                  { screenSize: 0, truncate: 10 },
                  { screenSize: 320, truncate: 14 },
                  { screenSize: 480, truncate: 20 },
                  { screenSize: 768, truncate: 30 },
                ]}
              />
            </div>
            <div className='text-main-secondary vw:text-12 font-bold'>{`${formatNumber(
              userProfile?.balance && userProfile?.balance >= 0
                ? userProfile?.balance
                : 0 || 0
            )} ฿`}</div>
          </div>
          <Link href='/account/info' onClick={() => closeDrawer()}>
            <div className='bg-main-bg vw:h-[40px] vw:w-[40px] border-neutral-3 relative overflow-hidden rounded-full border-2'>
              <img
                className='object-cover'
                src={userProfile?.avatar || '/img/header/avatar.png'}
                alt=''
              />
            </div>
          </Link>
          {/* </Badge> */}
          {renderIcon?.({
            onClick: () => {
              setIsShowNoti(true);
              setOpen(false);
            },
          })}
          <div className='relative'>
            {/* <svg
              onClick={() => {
                setIsShowNoti(true);
              }}
              className='vw:mx-2 vw:w-6 vw:h-6 !mr-0'
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M2 16.5959C2 16.2151 2.15471 15.8506 2.42864 15.586L3.45759 14.5922C3.84928 14.2139 4.06977 13.6922 4.06814 13.1476L4.05867 9.9946C4.04543 5.58319 7.61789 2 12.0293 2C16.4314 2 20 5.56859 20 9.97067L20 13.1716C20 13.702 20.2107 14.2107 20.5858 14.5858L21.5858 15.5858C21.851 15.851 22 16.2107 22 16.5858C22 17.3668 21.3668 18 20.5858 18H16C16 20.2091 14.2091 22 12 22C9.79086 22 8 20.2091 8 18H3.40408C2.62863 18 2 17.3714 2 16.5959ZM10 18C10 19.1046 10.8954 20 12 20C13.1046 20 14 19.1046 14 18H10ZM18 13.1716C18 14.2324 18.4214 15.2499 19.1716 16L4.87851 16C5.64222 15.246 6.07136 14.2161 6.06813 13.1416L6.05867 9.9886C6.04875 6.6841 8.7248 4 12.0293 4C15.3268 4 18 6.67316 18 9.97067L18 13.1716Z'
                fill='#845D00'
              />
            </svg>
            <div className='bg-error-base vw:w-2 vw:h-2 vw:border-1 absolute bottom-0 right-0 rounded-full border border-white' /> */}
          </div>
        </div>
      );
    }
    return (
      <ButtonCore
        onClick={() => {
          window?.popup?.login();
        }}
        className={clsx(
          'vw:!min-w-[101px] vw:!h-[38px] vw:!text-10 !bg-main-pri btn-11 ml-auto !max-h-[90%] !rounded-[32px] !font-bold',
          className
        )}
        size='large'
      >
        {t('header.loginSignUpBtn')}
      </ButtonCore>
    );
  };

  const renderDrawerUserInfo = () => {
    return (
      <div className=''>
        <div className='vw:pt-20'></div>
        {userProfile && (
          <>
            <div className='bg-linear-gray mt-3 rounded-2xl p-3'>
              <div className='flex items-center'>
                <img
                  className='w-[45px]'
                  src='/img/header/wallet.png'
                  alt='wallet'
                />
                <div className='text-main-secondary ml-3 text-[24px] font-bold'>
                  {`${formatNumber(
                    userProfile?.balance && userProfile?.balance >= 0
                      ? userProfile?.balance
                      : 0 || 0
                  )} ฿`}
                </div>
              </div>
              <div>
                <div className='mt-3 flex items-center gap-[8px]'>
                  <Link className='w-1/2' href='/account/deposit'>
                    <ButtonCore
                      onClick={() => closeDrawer()}
                      size='large'
                      type='secondary'
                      className='vw:!h-11 vw:!text-[13px] vw:!rounded-[32px] !bg-main-secondary w-full  !font-bold !text-white'
                    >
                      {t('headerMobile.userInfo.deposit')}
                    </ButtonCore>
                  </Link>
                  <Link className='w-1/2' href='/account/withdraw'>
                    <ButtonCore
                      onClick={() => closeDrawer()}
                      size='large'
                      className='vw:!h-11 vw:!text-[13px] vw:!rounded-[32px] !bg-main-pri w-full !font-bold !text-black'
                    >
                      {t('headerMobile.userInfo.withdraw')}
                    </ButtonCore>
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderNavMenu = () => {
    return (
      <Menu
        className='menuMobile !border-0'
        onClick={onMenuClick}
        defaultSelectedKeys={['1']}
        defaultOpenKeys={['sub1']}
        mode='inline'
        selectedKeys={[lng === 'th' ? 'thai' : 'eng']}
      >
        {userProfile && (
          <>
            <Menu.Item
              key='profile'
              icon={
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='!h-6 !w-6'
                  width={17}
                  height={16}
                  viewBox='0 0 17 16'
                  fill='none'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M8.28273 6.66658C9.3873 6.66658 10.2827 5.77115 10.2827 4.66658C10.2827 3.56202 9.3873 2.66659 8.28273 2.66659C7.17817 2.66659 6.28274 3.56202 6.28274 4.66658C6.28274 5.77115 7.17817 6.66658 8.28273 6.66658ZM8.28273 7.99992C10.1237 7.99992 11.6161 6.50753 11.6161 4.66658C11.6161 2.82564 10.1237 1.33325 8.28273 1.33325C6.44179 1.33325 4.9494 2.82564 4.9494 4.66658C4.9494 6.50753 6.44179 7.99992 8.28273 7.99992Z'
                    fill='#845D00'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M6.28271 10.6666C4.80996 10.6666 3.61605 11.8605 3.61605 13.3333V13.9999C3.61605 14.3681 3.31757 14.6666 2.94938 14.6666C2.58119 14.6666 2.28271 14.3681 2.28271 13.9999V13.3333C2.28271 11.1241 4.07358 9.33325 6.28271 9.33325H10.2827C12.4919 9.33325 14.2827 11.1241 14.2827 13.3333V13.9999C14.2827 14.3681 13.9842 14.6666 13.616 14.6666C13.2479 14.6666 12.9494 14.3681 12.9494 13.9999V13.3333C12.9494 11.8605 11.7555 10.6666 10.2827 10.6666H6.28271Z'
                    fill='#845D00'
                  />
                </svg>
              }
            >
              <div className='flex items-center'>
                <Link
                  className='vw:text-16 font-semibold'
                  onClick={() => closeDrawer()}
                  href='/account/info'
                >
                  {t('headerMobile.userInfo.navMenu1')}
                </Link>
                <div
                  className='vw:top-[4px] absolute right-2 z-20 ml-auto flex items-center'
                  onClick={() => closeDrawer()}
                >
                  <UserKycStatus />
                </div>
              </div>
            </Menu.Item>
            <Menu.Item
              key='transactionHistory'
              icon={
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='!h-6 !w-6'
                  width='17'
                  height='16'
                  viewBox='0 0 17 16'
                  fill='none'
                >
                  <path
                    d='M11.0875 1.86201C10.8271 1.60166 10.405 1.60166 10.1446 1.86201C9.8843 2.12236 9.88429 2.54447 10.1446 2.80482L10.6707 3.33085H6.94938C4.37205 3.33085 2.28271 5.42019 2.28271 7.99752V8.66418C2.28271 9.03237 2.58119 9.33085 2.94938 9.33085C3.31757 9.33085 3.61605 9.03237 3.61605 8.66418V7.99752C3.61605 6.15657 5.10843 4.66418 6.94938 4.66418H10.6738L10.1446 5.19336C9.88429 5.45372 9.8843 5.87583 10.1446 6.13617C10.405 6.39652 10.8271 6.39652 11.0875 6.13616L12.5174 4.70618C12.9079 4.31566 12.9079 3.6825 12.5174 3.29198L11.0875 1.86201Z'
                    fill='#845D00'
                  />
                  <path
                    d='M9.615 12.6667C12.1923 12.6667 14.2817 10.5774 14.2817 8.00008V7.33341C14.2817 6.96522 13.9832 6.66675 13.615 6.66675C13.2468 6.66675 12.9483 6.96522 12.9483 7.33341V8.00008C12.9483 9.84103 11.456 11.3334 9.615 11.3334H5.89054L6.41975 10.8042C6.68009 10.5438 6.68009 10.1217 6.41974 9.86139C6.15939 9.60104 5.73728 9.60104 5.47693 9.86139L4.04698 11.2914C3.65646 11.6819 3.65646 12.3151 4.04698 12.7056L5.47693 14.1355C5.73728 14.3959 6.15939 14.3959 6.41974 14.1356C6.68009 13.8752 6.68009 13.4531 6.41974 13.1927L5.89376 12.6667H9.615Z'
                    fill='#845D00'
                  />
                </svg>
              }
            >
              <Link
                onClick={() => closeDrawer()}
                href='/account/history-payment'
                className='vw:text-16 font-semibold'
              >
                {t('headerMobile.userInfo.navMenu2')}
              </Link>
            </Menu.Item>

            <Menu.Item
              key='bettingHistory'
              icon={
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='!h-6 !w-6'
                  width={17}
                  height={16}
                  viewBox='0 0 17 16'
                  fill='none'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M12.9494 7.33333H6.28271C5.91453 7.33333 5.61605 7.63181 5.61605 8V10.6667C5.61605 11.0349 5.91453 11.3333 6.28271 11.3333H12.9494C13.3176 11.3333 13.616 11.0349 13.616 10.6667V8C13.616 7.63181 13.3176 7.33333 12.9494 7.33333ZM6.28271 6C5.17815 6 4.28271 6.89543 4.28271 8V10.6667C4.28271 11.7712 5.17815 12.6667 6.28271 12.6667H12.9494C14.054 12.6667 14.9494 11.7712 14.9494 10.6667V8C14.9494 6.89543 14.054 6 12.9494 6H6.28271Z'
                    fill='#845D00'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M3.61603 4.66659H10.2827C10.6509 4.66659 10.9494 4.96506 10.9494 5.33325V5.99992H12.2827V5.33325C12.2827 4.22868 11.3873 3.33325 10.2827 3.33325H3.61603C2.51146 3.33325 1.61603 4.22868 1.61603 5.33325V7.99992C1.61603 9.10449 2.51146 9.99992 3.61603 9.99992H4.28269V8.66659H3.61603C3.24784 8.66659 2.94936 8.36811 2.94936 7.99992V5.33325C2.94936 4.96506 3.24784 4.66659 3.61603 4.66659Z'
                    fill='#845D00'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M9.61605 10.6667C10.3524 10.6667 10.9494 10.0697 10.9494 9.33333C10.9494 8.59695 10.3524 8 9.61605 8C8.87967 8 8.28271 8.59695 8.28271 9.33333C8.28271 10.0697 8.87967 10.6667 9.61605 10.6667Z'
                    fill='#845D00'
                  />
                  <path
                    d='M7.61605 9.33341C7.61605 9.7016 7.31757 10.0001 6.94938 10.0001C6.58119 10.0001 6.28271 9.7016 6.28271 9.33341C6.28271 8.96522 6.58119 8.66675 6.94938 8.66675C7.31757 8.66675 7.61605 8.96522 7.61605 9.33341Z'
                    fill='#845D00'
                  />
                  <path
                    d='M12.9494 9.33341C12.9494 9.7016 12.6509 10.0001 12.2827 10.0001C11.9145 10.0001 11.616 9.7016 11.616 9.33341C11.616 8.96522 11.9145 8.66675 12.2827 8.66675C12.6509 8.66675 12.9494 8.96522 12.9494 9.33341Z'
                    fill='#845D00'
                  />
                </svg>
              }
            >
              <Link
                className='vw:text-16 font-semibold'
                onClick={() => closeDrawer()}
                href='/account/history-bet'
              >
                {t('headerMobile.userInfo.navMenu3')}
              </Link>
            </Menu.Item>
            <Divider className='!m-0' />

            <Menu.Item
              key='bonusManagement'
              icon={
                <svg
                  className='!h-6 !w-6'
                  xmlns='http://www.w3.org/2000/svg'
                  width={17}
                  height={16}
                  viewBox='0 0 17 16'
                  fill='none'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M8.28269 2.99992L7.49011 2.00919C7.14833 1.58196 6.63086 1.33325 6.08374 1.33325H5.94936C5.02889 1.33325 4.28269 2.07944 4.28269 2.99992C4.28269 3.11407 4.29417 3.22555 4.31603 3.33325H3.61603C2.51146 3.33325 1.61603 4.22868 1.61603 5.33325V5.99992C1.61603 6.59225 1.87353 7.12444 2.28269 7.49066V12.6666C2.28269 13.7712 3.17813 14.6666 4.28269 14.6666H12.2827C13.3873 14.6666 14.2827 13.7712 14.2827 12.6666V7.49066C14.6919 7.12444 14.9494 6.59225 14.9494 5.99992V5.33325C14.9494 4.22868 14.0539 3.33325 12.9494 3.33325H12.2494C12.2712 3.22555 12.2827 3.11407 12.2827 2.99992C12.2827 2.07944 11.5365 1.33325 10.616 1.33325H10.4817C9.93453 1.33325 9.41706 1.58196 9.07528 2.00919L8.28269 2.99992ZM9.72353 3.33325H10.616C10.8001 3.33325 10.9494 3.18401 10.9494 2.99992C10.9494 2.81582 10.8001 2.66659 10.616 2.66659H10.4817C10.3396 2.66659 10.2052 2.73117 10.1164 2.84212L9.72353 3.33325ZM3.61603 4.66659C3.24784 4.66659 2.94936 4.96506 2.94936 5.33325V5.99992C2.94936 6.36811 3.24784 6.66659 3.61603 6.66659H7.61603V4.66659H3.61603ZM8.94936 4.66659V6.66659H12.9494C13.3176 6.66659 13.616 6.36811 13.616 5.99992V5.33325C13.616 4.96506 13.3176 4.66659 12.9494 4.66659H8.94936ZM5.61603 2.99992C5.61603 3.18401 5.76527 3.33325 5.94936 3.33325H6.84186L6.44896 2.84212C6.3602 2.73117 6.22582 2.66659 6.08374 2.66659H5.94936C5.76527 2.66659 5.61603 2.81582 5.61603 2.99992ZM3.61603 7.99992L3.61603 12.6666C3.61603 13.0348 3.9145 13.3333 4.28269 13.3333H7.61603V7.99992H3.61603ZM8.94936 13.3333V7.99992H12.9494V12.6666C12.9494 13.0348 12.6509 13.3333 12.2827 13.3333H8.94936Z'
                    fill='#845D00'
                  />
                </svg>
              }
            >
              <Link
                href='/account/promotion'
                className='vw:text-16 font-semibold'
                onClick={() => closeDrawer()}
              >
                {t('headerMobile.userInfo.navMenu4')}
              </Link>
            </Menu.Item>
            {/*<Menu.Item*/}
            {/*  key='vipProgram'*/}
            {/*  icon={*/}
            {/*    <svg*/}
            {/*      xmlns='http://www.w3.org/2000/svg'*/}
            {/*      width={17}*/}
            {/*      height={16}*/}
            {/*      viewBox='0 0 17 16'*/}
            {/*      fill='none'*/}
            {/*    >*/}
            {/*      <path*/}
            {/*        fillRule='evenodd'*/}
            {/*        clipRule='evenodd'*/}
            {/*        d='M15.0559 4.78938L13.1045 2.14555C12.7275 1.63471 12.1303 1.33325 11.4954 1.33325H5.07012C4.43521 1.33325 3.83801 1.63472 3.46097 2.14555L1.50957 4.78938C0.980412 5.5063 0.989357 6.48681 1.5315 7.19395L6.69553 13.9296C7.49607 14.9738 9.06941 14.9738 9.86995 13.9296L15.034 7.19395C15.5761 6.48681 15.5851 5.5063 15.0559 4.78938ZM2.76533 5.33325L4.53373 2.93735C4.65941 2.76707 4.85848 2.66659 5.07012 2.66659H11.4954C11.707 2.66659 11.9061 2.76707 12.0317 2.93735L13.8002 5.33325H2.76533ZM2.80728 6.66659L7.75367 13.1184C8.02052 13.4665 8.54497 13.4665 8.81181 13.1184L13.7582 6.66659H2.80728Z'*/}
            {/*        fill='#845D00'*/}
            {/*      />*/}
            {/*    </svg>*/}
            {/*  }*/}
            {/*>*/}
            {/*  {t('headerMobile.userInfo.navMenu5')}*/}
            {/*</Menu.Item>*/}
            {/* <Menu.Item
              key='inviteToEarn'
              icon={
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={17}
                  height={16}
                  viewBox='0 0 17 16'
                  fill='none'
                >
                  <path
                    d='M9.94916 7.31263C11.2647 7.14869 12.2827 6.02651 12.2827 4.66657C12.2827 3.30662 11.2647 2.18445 9.94916 2.02051C10.5716 2.72567 10.9494 3.65201 10.9494 4.66657C10.9494 5.68113 10.5716 6.60747 9.94916 7.31263Z'
                    fill='#845D00'
                  />
                  <path
                    d='M13.6161 13.3333C13.6161 13.7015 13.9146 14 14.2827 14C14.6509 14 14.9494 13.7015 14.9494 13.3333V12C14.9494 10.2266 13.5645 8.77663 11.8173 8.67261C12.5187 9.29378 13.0553 10.0969 13.3531 11.008C13.5204 11.3003 13.6161 11.639 13.6161 12V13.3333Z'
                    fill='#845D00'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M4.94936 10.0001C3.84479 10.0001 2.94936 10.8955 2.94936 12.0001V13.3334C2.94936 13.7016 2.65088 14.0001 2.28269 14.0001C1.9145 14.0001 1.61603 13.7016 1.61603 13.3334V12.0001C1.61603 10.1591 3.10841 8.66675 4.94936 8.66675H8.94936C10.7903 8.66675 12.2827 10.1591 12.2827 12.0001V13.3334C12.2827 13.7016 11.9842 14.0001 11.616 14.0001C11.2478 14.0001 10.9494 13.7016 10.9494 13.3334V12.0001C10.9494 10.8955 10.0539 10.0001 8.94936 10.0001H4.94936Z'
                    fill='#845D00'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M6.94938 3.33333C6.213 3.33333 5.61605 3.93029 5.61605 4.66667C5.61605 5.40305 6.213 6 6.94938 6C7.68576 6 8.28271 5.40305 8.28271 4.66667C8.28271 3.93029 7.68576 3.33333 6.94938 3.33333ZM4.28271 4.66667C4.28271 3.19391 5.47662 2 6.94938 2C8.42214 2 9.61605 3.19391 9.61605 4.66667C9.61605 6.13943 8.42214 7.33333 6.94938 7.33333C5.47662 7.33333 4.28271 6.13943 4.28271 4.66667Z'
                    fill='#845D00'
                  />
                </svg>
              }
            > */}
            {/* <Link href='/account/referral' onClick={() => closeDrawer()}>
                {t('headerMobile.userInfo.navMenu6')}
              </Link> */}
            {/* </Menu.Item> */}
            <Divider className='!m-0' />
          </>
        )}

        <Menu.Item
          key='language'
          title={t('headerMobile.userInfo.navMenu8')}
          icon={
            <>
              <svg
                className='mr-2 !h-6 !w-6'
                xmlns='http://www.w3.org/2000/svg'
                width={17}
                height={16}
                viewBox='0 0 17 16'
                fill='none'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M8.28269 1.33325C4.6008 1.33325 1.61603 4.31802 1.61603 7.99992C1.61603 11.6818 4.6008 14.6666 8.28269 14.6666C11.9646 14.6666 14.9494 11.6818 14.9494 7.99992C14.9494 4.31802 11.9646 1.33325 8.28269 1.33325ZM3.11738 6.66659C3.0077 7.09275 2.94936 7.53952 2.94936 7.99992C2.94936 8.46032 3.0077 8.90709 3.11738 9.33325H5.01224C4.97088 8.9013 4.94936 8.45517 4.94936 7.99992C4.94936 7.54466 4.97088 7.09854 5.01224 6.66659H3.11738ZM3.66287 5.33325H5.21148C5.35279 4.65092 5.54777 4.02673 5.78787 3.48652C5.82804 3.39613 5.86979 3.3074 5.9131 3.22058C4.9713 3.68842 4.18742 4.42645 3.66287 5.33325ZM6.35233 6.66659C6.30704 7.09164 6.28269 7.53805 6.28269 7.99992C6.28269 8.46179 6.30704 8.9082 6.35233 9.33325H10.2131C10.2584 8.9082 10.2827 8.46179 10.2827 7.99992C10.2827 7.53805 10.2584 7.09164 10.2131 6.66659H6.35233ZM9.98827 5.33325H6.57712C6.6929 4.84502 6.83856 4.40542 7.00628 4.02804C7.22494 3.53606 7.46804 3.1792 7.7043 2.95436C7.93712 2.73278 8.13143 2.66659 8.28269 2.66659C8.43396 2.66659 8.62827 2.73278 8.86109 2.95436C9.09735 3.1792 9.34045 3.53606 9.5591 4.02804C9.72683 4.40542 9.87249 4.84502 9.98827 5.33325ZM11.5531 6.66659C11.5945 7.09854 11.616 7.54466 11.616 7.99992C11.616 8.45517 11.5945 8.9013 11.5531 9.33325H13.448C13.5577 8.90709 13.616 8.46032 13.616 7.99992C13.616 7.53952 13.5577 7.09275 13.448 6.66659H11.5531ZM12.9025 5.33325H11.3539C11.2126 4.65092 11.0176 4.02673 10.7775 3.48652C10.7373 3.39613 10.6956 3.3074 10.6523 3.22058C11.5941 3.68842 12.378 4.42645 12.9025 5.33325ZM5.9131 12.7793C4.9713 12.3114 4.18742 11.5734 3.66287 10.6666H5.21148C5.35279 11.3489 5.54777 11.9731 5.78787 12.5133C5.82804 12.6037 5.86979 12.6924 5.9131 12.7793ZM7.00628 11.9718C6.83856 11.5944 6.6929 11.1548 6.57712 10.6666H9.98827C9.87249 11.1548 9.72683 11.5944 9.5591 11.9718C9.34045 12.4638 9.09735 12.8206 8.86109 13.0455C8.62827 13.2671 8.43396 13.3333 8.28269 13.3333C8.13143 13.3333 7.93712 13.2671 7.7043 13.0455C7.46804 12.8206 7.22494 12.4638 7.00628 11.9718ZM10.7775 12.5133C11.0176 11.9731 11.2126 11.3489 11.3539 10.6666H12.9025C12.378 11.5734 11.5941 12.3114 10.6523 12.7793C10.6956 12.6924 10.7373 12.6037 10.7775 12.5133Z'
                  fill='#845D00'
                />
              </svg>
              <div className='vw:text-16 font-bold font-semibold'>
                {t('headerMobile.userInfo.navMenu8')}
              </div>
            </>
          }
        >
          <div className='absolute right-2 top-1 z-20 ml-auto flex scale-[0.8] items-center text-right'>
            <SwitchLanguage></SwitchLanguage>
          </div>
        </Menu.Item>
        {/* <Menu.SubMenu
          key='language'
          title={t('headerMobile.userInfo.navMenu8')}
          icon={
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width={17}
              height={16}
              viewBox='0 0 17 16'
              fill='none'
            >
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M8.28269 1.33325C4.6008 1.33325 1.61603 4.31802 1.61603 7.99992C1.61603 11.6818 4.6008 14.6666 8.28269 14.6666C11.9646 14.6666 14.9494 11.6818 14.9494 7.99992C14.9494 4.31802 11.9646 1.33325 8.28269 1.33325ZM3.11738 6.66659C3.0077 7.09275 2.94936 7.53952 2.94936 7.99992C2.94936 8.46032 3.0077 8.90709 3.11738 9.33325H5.01224C4.97088 8.9013 4.94936 8.45517 4.94936 7.99992C4.94936 7.54466 4.97088 7.09854 5.01224 6.66659H3.11738ZM3.66287 5.33325H5.21148C5.35279 4.65092 5.54777 4.02673 5.78787 3.48652C5.82804 3.39613 5.86979 3.3074 5.9131 3.22058C4.9713 3.68842 4.18742 4.42645 3.66287 5.33325ZM6.35233 6.66659C6.30704 7.09164 6.28269 7.53805 6.28269 7.99992C6.28269 8.46179 6.30704 8.9082 6.35233 9.33325H10.2131C10.2584 8.9082 10.2827 8.46179 10.2827 7.99992C10.2827 7.53805 10.2584 7.09164 10.2131 6.66659H6.35233ZM9.98827 5.33325H6.57712C6.6929 4.84502 6.83856 4.40542 7.00628 4.02804C7.22494 3.53606 7.46804 3.1792 7.7043 2.95436C7.93712 2.73278 8.13143 2.66659 8.28269 2.66659C8.43396 2.66659 8.62827 2.73278 8.86109 2.95436C9.09735 3.1792 9.34045 3.53606 9.5591 4.02804C9.72683 4.40542 9.87249 4.84502 9.98827 5.33325ZM11.5531 6.66659C11.5945 7.09854 11.616 7.54466 11.616 7.99992C11.616 8.45517 11.5945 8.9013 11.5531 9.33325H13.448C13.5577 8.90709 13.616 8.46032 13.616 7.99992C13.616 7.53952 13.5577 7.09275 13.448 6.66659H11.5531ZM12.9025 5.33325H11.3539C11.2126 4.65092 11.0176 4.02673 10.7775 3.48652C10.7373 3.39613 10.6956 3.3074 10.6523 3.22058C11.5941 3.68842 12.378 4.42645 12.9025 5.33325ZM5.9131 12.7793C4.9713 12.3114 4.18742 11.5734 3.66287 10.6666H5.21148C5.35279 11.3489 5.54777 11.9731 5.78787 12.5133C5.82804 12.6037 5.86979 12.6924 5.9131 12.7793ZM7.00628 11.9718C6.83856 11.5944 6.6929 11.1548 6.57712 10.6666H9.98827C9.87249 11.1548 9.72683 11.5944 9.5591 11.9718C9.34045 12.4638 9.09735 12.8206 8.86109 13.0455C8.62827 13.2671 8.43396 13.3333 8.28269 13.3333C8.13143 13.3333 7.93712 13.2671 7.7043 13.0455C7.46804 12.8206 7.22494 12.4638 7.00628 11.9718ZM10.7775 12.5133C11.0176 11.9731 11.2126 11.3489 11.3539 10.6666H12.9025C12.378 11.5734 11.5941 12.3114 10.6523 12.7793C10.6956 12.6924 10.7373 12.6037 10.7775 12.5133Z'
                fill='#845D00'
              />
            </svg>
          }
        > */}

        {/* <Menu.Item disabled={loading} key='thai'>
            <div className='flex items-center'>
              <span className='mr-auto'>Thai</span>
              {loading && lng === 'en' && (
                <Spin
                  className='!absolute !left-1/2'
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
              )}
              <img className='h-6 w-6' src='/img/header/thai.svg' alt='' />
            </div>
          </Menu.Item>
          <Menu.Item disabled={loading} key='eng'>
            <div className='flex items-center'>
              <span className='mr-auto'>English</span>
              {loading && lng === 'th' && (
                <Spin
                  className='!absolute !left-1/2'
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
              )}
              <img className='h-6 w-6' src='/img/header/eng.svg' alt='' />
            </div>
          </Menu.Item> */}
        {/* </Menu.SubMenu> */}
        <Menu.SubMenu
          key='support'
          title={
            <span className='vw:text-16 font-semibold'>
              {t('headerMobile.userInfo.navMenu9')}
            </span>
          }
          icon={
            <svg
              className='!h-6 !w-6'
              xmlns='http://www.w3.org/2000/svg'
              width={17}
              height={16}
              viewBox='0 0 17 16'
              fill='none'
            >
              <path
                d='M10.9494 3.99998C11.2126 3.99998 11.47 3.92206 11.689 3.77605L13.616 2.49134V8.66665C13.616 9.03484 13.3176 9.33331 12.9494 9.33331H12.2827V10.6666H12.9494C14.054 10.6666 14.9494 9.77122 14.9494 8.66665V2.49134C14.9494 1.42641 13.7625 0.791221 12.8764 1.38194L10.9494 2.66665H6.28271C5.17815 2.66665 4.28271 3.56208 4.28271 4.66665V5.33331H5.61605V4.66665C5.61605 4.29846 5.91453 3.99998 6.28271 3.99998H10.9494Z'
                fill='#845D00'
              />
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M4.11643 12.2922C4.35285 12.1031 4.6466 12 4.94936 12H10.2827C10.6509 12 10.9494 11.7016 10.9494 11.3334V7.33337C10.9494 6.96518 10.6509 6.66671 10.2827 6.66671H3.61603C3.24784 6.66671 2.94936 6.96518 2.94936 7.33337V13.2259L4.11643 12.2922ZM4.94936 13.3334H10.2827C11.3873 13.3334 12.2827 12.4379 12.2827 11.3334V7.33337C12.2827 6.2288 11.3873 5.33337 10.2827 5.33337H3.61603C2.51146 5.33337 1.61603 6.2288 1.61603 7.33337V13.2259C1.61603 14.3439 2.90927 14.9654 3.78229 14.267L4.94936 13.3334Z'
                fill='#845D00'
              />
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M4.28271 9.33329C4.28271 8.9651 4.58119 8.66663 4.94938 8.66663H8.94938C9.31757 8.66663 9.61605 8.9651 9.61605 9.33329C9.61605 9.70148 9.31757 9.99996 8.94938 9.99996H4.94938C4.58119 9.99996 4.28271 9.70148 4.28271 9.33329Z'
                fill='#845D00'
              />
            </svg>
          }
        >
          <Menu.Item key='line'>
            <a
              target='_blank'
              rel='noopener nofollow'
              href='https://line.me/R/ti/p/@leo88support?from=page&amp;searchId=leo88support'
              aria-label='navigate to Line App'
            >
              <div className='flex items-center '>
                <span className='vw:text-16 mr-auto font-semibold'>
                  Line support
                </span>

                <svg
                  className='!h-6 !w-6'
                  xmlns='http://www.w3.org/2000/svg'
                  width='30'
                  height='12'
                  viewBox='0 0 30 12'
                  fill='none'
                >
                  <path
                    fill-rule='evenodd'
                    clip-rule='evenodd'
                    d='M1.20178 11.0011H5.84926C6.51034 11.0011 7.05104 10.4764 7.05104 9.83501V9.73775C7.05104 9.0964 6.51034 8.57184 5.84926 8.57184H2.50381V1.18764C2.50381 0.546291 1.96311 0.0217285 1.30203 0.0217285H1.20178C0.540699 0.0217285 0 0.546291 0 1.18764V9.83501C0 10.4764 0.540699 11.0011 1.20178 11.0011ZM30 5.58061V5.48334C30 4.84199 29.4591 4.31743 28.7981 4.31743H25.4528V2.47146H28.7981C29.4591 2.47146 30 1.9469 30 1.30555V1.20828C30 0.566938 29.4591 0.0423755 28.7981 0.0423755H24.1506C23.4895 0.0423755 22.9488 0.566938 22.9488 1.20828V9.85566C22.9488 10.497 23.4895 11.0217 24.1506 11.0217H28.7981C29.4591 11.0217 30 10.497 30 9.85566V9.75839C30 9.11704 29.4591 8.59248 28.7981 8.59248H25.4528V6.74651H28.7981C29.4591 6.74651 30 6.22195 30 5.58061ZM21.0623 10.6535L21.0627 10.653C21.2851 10.4355 21.4099 10.1416 21.4102 9.83517V1.18764C21.4102 0.546291 20.8695 0.0217285 20.2082 0.0217285H20.108C19.4469 0.0217285 18.9062 0.546291 18.9062 1.18764V6.26373L14.5625 0.611297C14.355 0.259975 13.9642 0.0217285 13.519 0.0217285H13.4187C12.7576 0.0217285 12.2168 0.546291 12.2168 1.18764V9.83501C12.2168 10.4764 12.7575 11.0011 13.4187 11.0011H13.519C14.1801 11.0011 14.7208 10.4764 14.7208 9.83501V4.66407L19.0959 10.4615C19.1228 10.5022 19.1523 10.5412 19.184 10.5783L19.1842 10.5786C19.3044 10.7377 19.4688 10.8467 19.6521 10.9133C19.7929 10.9698 19.9468 11.0011 20.108 11.0011H20.2082C20.4041 11.0012 20.5969 10.9544 20.7697 10.8649C20.8897 10.8093 20.991 10.7378 21.0623 10.6535ZM9.22281 11.0011H9.32307C9.98415 11.0011 10.5248 10.4764 10.5248 9.83501V1.18764C10.5247 0.546291 9.98398 0.0217285 9.32291 0.0217285H9.22281C8.56174 0.0217285 8.02087 0.546291 8.02087 1.18764V9.83501C8.02087 10.4764 8.56174 11.0011 9.22281 11.0011Z'
                    fill='#845D00'
                  />
                </svg>
              </div>
            </a>
          </Menu.Item>
          <Menu.Item key='live'>
            <a
              target='_blank'
              rel='noopener nofollow'
              href='https://direct.lc.chat/16386009/'
              aria-label='navigate to Live chat 24/7'
            >
              <div className='flex items-center'>
                <span className='vw:text-16 mr-auto font-semibold'>
                  Live chat 24/7
                </span>
                <svg
                  className='!h-10 !w-8'
                  xmlns='http://www.w3.org/2000/svg'
                  width='40'
                  height='41'
                  viewBox='0 0 40 41'
                  fill='none'
                >
                  <path
                    fill-rule='evenodd'
                    clip-rule='evenodd'
                    d='M14.6 15.933V17.356H13.25C12.0074 17.356 11 18.4179 11 19.7277V29.5743C11 30.7675 12.3094 31.4309 13.1933 30.6855L15.3767 28.8443C15.4565 28.777 15.5557 28.7403 15.6579 28.7403H23.15C24.3926 28.7403 25.4 27.6785 25.4 26.3686V24.9456H26.75C27.9926 24.9456 29 23.8837 29 22.5738V12.4254C29 11.2889 27.7983 10.6109 26.9012 11.2414L23.7134 13.4816C23.6394 13.5335 23.5526 13.5612 23.4637 13.5612H16.85C15.6074 13.5612 14.6 14.6231 14.6 15.933ZM23.15 17.356H16.4V15.933C16.4 15.671 16.6015 15.4586 16.85 15.4586H23.4637C23.908 15.4586 24.3422 15.32 24.7118 15.0603L27.2 13.3118V22.5738C27.2 22.8358 26.9985 23.0482 26.75 23.0482H25.4V19.7277C25.4 18.4179 24.3926 17.356 23.15 17.356ZM15.5 22.0995C15.0029 22.0995 14.6 22.5242 14.6 23.0482C14.6 23.5721 15.0029 23.9969 15.5 23.9969H20.9C21.3971 23.9969 21.8 23.5721 21.8 23.0482C21.8 22.5242 21.3971 22.0995 20.9 22.0995H15.5Z'
                    fill='#845D00'
                  />
                </svg>
              </div>
            </a>
          </Menu.Item>
        </Menu.SubMenu>
        {userProfile && (
          <Menu.Item
            key='logout'
            icon={
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  d='M17.7071 7.79289C17.3166 7.40237 16.6834 7.40237 16.2929 7.79289C15.9024 8.18342 15.9024 8.81658 16.2929 9.20711L18.0858 11H9C8.44772 11 8 11.4477 8 12C8 12.5523 8.44772 13 9 13H18.0858L16.2929 14.7929C15.9024 15.1834 15.9024 15.8166 16.2929 16.2071C16.6834 16.5976 17.3166 16.5976 17.7071 16.2071L20.5 13.4142C21.2811 12.6332 21.281 11.3668 20.5 10.5858L17.7071 7.79289Z'
                  fill='#845D00'
                />
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M3 5C3 3.34315 4.34315 2 6 2H12C13.6569 2 15 3.34315 15 5V6C15 6.55228 14.5523 7 14 7C13.4477 7 13 6.55228 13 6V5C13 4.44772 12.5523 4 12 4H6C5.44772 4 5 4.44772 5 5V19C5 19.5523 5.44772 20 6 20H12C12.5523 20 13 19.5523 13 19V18C13 17.4477 13.4477 17 14 17C14.5523 17 15 17.4477 15 18V19C15 20.6569 13.6569 22 12 22H6C4.34315 22 3 20.6569 3 19V5Z'
                  fill='#845D00'
                />
              </svg>
            }
          >
            <div onClick={onLogout} className='vw:text-16 font-semibold'>
              {t('signOut')}
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const renderTopMenu = () => {
    return gameMenuMobile.map((_: any, index?: number) => {
      const isActive = _.slug === activeGame;

      return (
        <div
          key={_.slug || index}
          className={clsx(
            'vw:!h-[100px] vw:w-full vw:rounded-lg gap-2',
            isActive ? 'shadow-box-light' : 'shadow-box-light'
          )}
        >
          <Link
            href={`/?slug=${_.slug}`}
            onClick={() => {
              if (_.isOpenNewTab && token) {
                openGameProvider(_.games?.[0]);
              }
              setActiveGame(_.slug);
              window.homePage?.setGamePage(_.slug);
              window.homePage?.setCateDetail('hot');
              window.homePage?.setProvider('');
              window.history.pushState(null, '', `?slug=${_.slug}`);
              setTimeout(() => {
                closeDrawer();
              }, 200);
            }}
            key={_.slug}
            className={clsx(
              !isActive && '!border-2 !border-[#fff] grayscale',
              'vw:h-[100px] vw:w-full vw:rounded-lg flex w-full items-center justify-center  bg-[#FCFCFC] first:mt-0',
              isActive && 'bg-main-bg !border !border-[#fff]'
            )}
          >
            <div
              className={clsx(
                'vw:h-[50px] vw:rounded-lg flex w-full flex-col items-center justify-center'
              )}
            >
              <div
                className={clsx(
                  // isActive && 'active-icon pb-0.5',
                  'relative m-auto flex h-full w-full items-center justify-center'
                )}
              >
                <img
                  className={clsx(
                    !isActive && 'vw:max-h-[36px] vw:min-w-[36px]',
                    isActive &&
                      clsx(
                        'vw:max-h-[37px] vw:min-w-[37px] m-auto'
                        // 'active-menu-icon'
                      )
                  )}
                  src={_.icon}
                  alt={_.label}
                />
              </div>
              <span
                className={clsx(
                  'vw:text-[12px] vw:mt-2 whitespace-pre font-bold tracking-tighter',
                  isActive && 'vw:text-[12.5px]'
                )}
              >
                {t(_.label)}
              </span>
            </div>
          </Link>
        </div>
      );
    });
  };

  return (
    <header>
      <div className='vw:h-[64px] fixed top-0 z-[1000] w-full max-w-[100vw] bg-white'>
        <div className='bg-linear vw:h-[64px] flex max-w-full flex-row items-center justify-between px-4'>
          <Link
            className='flex h-[31px] w-[139px] flex-row items-center'
            href='/?slug=hot'
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.homePage?.setGamePage('hot');
                window.homePage?.setCateDetail('all');
                window.homePage?.setProvider('');
                window?.popup?.closeModal();
                setActiveGame('');
                closeDrawer();
                window.scrollTo(0, 0);
              }
            }}
          >
            <Image
              priority
              alt='logo'
              height={31}
              width={139}
              className='vw:w-[139px]'
              src='/img/logo/main-logo.svg'
            />
          </Link>

          {isLoading && (
            <div className='vw:h-[64px] ml-auto mt-8 flex'>
              <div className='mr-3'>
                <Skeleton
                  className='end mb-1 ml-auto flex justify-end text-right'
                  active
                  round
                  title={false}
                  paragraph={{ rows: 1, width: 50 }}
                />
                <Skeleton
                  className='end ml-auto mt-1 flex justify-end text-right'
                  active
                  round
                  title={false}
                  paragraph={{ rows: 1, width: 50 }}
                />
              </div>
              <SkeletonAvatar className='ml-auto mr-2 h-40 w-40' active />
            </div>
          )}
          {!isLoading && renderUserProfile()}
          <div
            className='flex items-center'
            onClick={() => {
              setOpen((prev) => !prev);
              setIsShowNoti(false);
            }}
          >
            <svg
              className='vw:w-6 vw:h-6 ml-2'
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M4 6C4 5.44772 4.44772 5 5 5H20C20.5523 5 21 5.44772 21 6C21 6.55228 20.5523 7 20 7H5C4.44772 7 4 6.55228 4 6Z'
                fill='#845D00'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M4 12C4 11.4477 4.44772 11 5 11H15C15.5523 11 16 11.4477 16 12C16 12.5523 15.5523 13 15 13H5C4.44772 13 4 12.5523 4 12Z'
                fill='#845D00'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M4 18C4 17.4477 4.44772 17 5 17H11C11.5523 17 12 17.4477 12 18C12 18.5523 11.5523 19 11 19H5C4.44772 19 4 18.5523 4 18Z'
                fill='#845D00'
              />
            </svg>
          </div>
        </div>
      </div>
      <div className='bg-main-pri vw:mt-[64px] min-h-6 flex w-full items-center px-3'>
        <svg
          className='shrink-0'
          fill='none'
          height='13'
          viewBox='0 0 12 13'
          width='12'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            clipRule='evenodd'
            d='M7.90482 7.69636C8.02188 7.6078 8.12602 7.502 8.21336 7.38179C8.39955 7.12552 8.49983 6.81688 8.49983 6.50011C8.49983 6.18334 8.39955 5.87471 8.21336 5.61844C8.12597 5.49816 8.02177 5.39231 7.90462 5.30372C7.87613 5.28217 7.84878 5.25878 7.82294 5.23382C7.79925 5.21093 7.77684 5.18673 7.75597 5.16141C7.62365 5.00087 7.5537 4.7955 7.61787 4.598C7.7032 4.33537 7.98891 4.18754 8.22935 4.32335C8.3262 4.37806 8.41923 4.43913 8.50781 4.50612M8.50781 4.50612C8.70298 4.65374 8.87676 4.83023 9.02238 5.03065C9.3327 5.45777 9.49983 5.97217 9.49983 6.50011C9.49983 7.02806 9.3327 7.54246 9.02238 7.96958C8.87673 8.17004 8.70304 8.34647 8.50781 8.4941C8.41923 8.5611 8.3262 8.62217 8.22935 8.67688C7.98891 8.81269 7.7032 8.66485 7.61787 8.40223C7.5537 8.20472 7.62365 7.99936 7.75597 7.83881C7.77684 7.81349 7.79925 7.78929 7.82294 7.76641C7.84878 7.74145 7.87632 7.71791 7.90482 7.69636'
            fill='#373737'
            fillRule='evenodd'
          />
          <path
            clipRule='evenodd'
            d='M8.94378 9.12979C9.32939 8.91808 9.66583 8.62298 9.92708 8.26339C10.2995 7.75084 10.5 7.13357 10.5 6.50003C10.5 5.86649 10.2995 5.24922 9.92708 4.73668C9.65321 4.35972 9.29673 4.05364 8.88758 3.8402C8.88694 3.83986 8.8863 3.83953 8.88567 3.8392C8.86794 3.82987 8.85073 3.81975 8.8341 3.80891C8.81603 3.79713 8.79864 3.7845 8.78198 3.77109C8.60314 3.62709 8.5094 3.39353 8.58159 3.17133C8.66693 2.90871 8.95083 2.76219 9.20076 2.8796C9.2511 2.90324 9.30088 2.92791 9.3501 2.95359C9.89561 3.23816 10.3709 3.64626 10.7361 4.14889C11.2326 4.83228 11.5 5.65531 11.5 6.50003C11.5 7.34475 11.2326 8.16778 10.7361 8.85117C10.3709 9.3538 9.89561 9.7619 9.3501 10.0465C9.30088 10.0722 9.2511 10.0968 9.20076 10.1205C8.95083 10.2379 8.66693 10.0914 8.58159 9.82873C8.5094 9.60654 8.60314 9.37297 8.78198 9.22897C8.79864 9.21556 8.81603 9.20293 8.8341 9.19115C8.85073 9.18031 8.86794 9.17019 8.88567 9.16087C8.8863 9.16053 8.88694 9.1602 8.88758 9.15987C8.90643 9.15004 8.92516 9.14001 8.94378 9.12979Z'
            fill='#373737'
            fillRule='evenodd'
          />
          <path
            d='M7 10.2963V2.70385C7 1.84469 5.98808 1.38551 5.3415 1.95127L3 4.00008H2C1.17157 4.00008 0.5 4.67166 0.5 5.50008V7.50008C0.5 8.32851 1.17157 9.00008 2 9.00008H3L5.3415 11.0489C5.98808 11.6147 7 11.1555 7 10.2963Z'
            fill='#373737'
          />
        </svg>
        <span className='text-12 min-h-6 ml-[10px] flex h-6 items-center text-[#292929]'>
          <Marquee
            loop={100}
            speed={15}
            autoFill
            pauseOnClick
            gradient
            gradientColor='#D3A300'
            gradientWidth={20}
          >
            <span className='pr-10'>{t('topSlider.marqueeContent')}</span>
          </Marquee>
        </span>
      </div>

      <Drawer
        zIndex={999}
        closable={false}
        onClose={() => {
          closeDrawer();
          setIsShowNoti(false);
        }}
        maskClassName='!bg-black !bg-opacity-80'
        open={open || isShowNoti}
        placement='right'
        destroyOnClose
        width='86.5%'
        // width='100%'
        styles={{
          body: {
            padding: 0,
            background: '#fcfcfc',
          },
        }}
      >
        {isShowNoti && !open && (
          <div className={clsx('vw:mt-20')}>
            <ButtonCore
              onClick={handleReadAll}
              className='!bg-neutral-1 vw:!h-[45px] mx-auto !w-[94%] !rounded-3xl !text-black'
            >
              <svg
                className='vw:w-4 vw:h-4 '
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M2 16.5959C2 16.2151 2.15471 15.8506 2.42864 15.586L3.45759 14.5922C3.84928 14.2139 4.06977 13.6922 4.06814 13.1476L4.05867 9.9946C4.04543 5.58319 7.61789 2 12.0293 2C16.4314 2 20 5.56859 20 9.97067L20 13.1716C20 13.702 20.2107 14.2107 20.5858 14.5858L21.5858 15.5858C21.851 15.851 22 16.2107 22 16.5858C22 17.3668 21.3668 18 20.5858 18H16C16 20.2091 14.2091 22 12 22C9.79086 22 8 20.2091 8 18H3.40408C2.62863 18 2 17.3714 2 16.5959ZM10 18C10 19.1046 10.8954 20 12 20C13.1046 20 14 19.1046 14 18H10ZM18 13.1716C18 14.2324 18.4214 15.2499 19.1716 16L4.87851 16C5.64222 15.246 6.07136 14.2161 6.06813 13.1416L6.05867 9.9886C6.04875 6.6841 8.7248 4 12.0293 4C15.3268 4 18 6.67316 18 9.97067L18 13.1716Z'
                  fill='#000000'
                />
              </svg>
              <span className='vw:text-14 !mx-2 font-bold'> Notification</span>
              {hasNotiUnRead && (
                <div className='bg-error-base vw:w-2 vw:h-2 vw:border-1 rounded-full border border-white' />
              )}
            </ButtonCore>
            <div className='vw:mt-3 vw:px-3'>
              {renderNotification?.(({ hasLink = false }) => {
                if (hasLink) {
                  setIsShowNoti(false);
                }
              })}
            </div>
          </div>
        )}
        {open && !isShowNoti && (
          <div className={clsx('px-3')}>
            {renderDrawerUserInfo()}
            <div className='vw:mt-3 grid w-full grid-cols-3 gap-3'>
              {renderTopMenu()}
            </div>
            <div
              className={clsx(
                'shadow-5 relative mt-3 flex h-auto flex-col rounded-2xl py-4 pb-6',
                {
                  '': userProfile,
                }
              )}
            >
              <div className='mb-8'>{renderNavMenu()}</div>
              <ButtonCore
                onClick={() => {
                  if (window.deferredPrompt) {
                    window.deferredPrompt.prompt();
                    window.deferredPrompt.userChoice.then(
                      (choiceResult: any) => {
                        if (choiceResult.outcome === 'accepted') {
                          // console.log('User accepted the A2HS prompt');
                        } else {
                          // console.log('User dismissed the A2HS prompt');
                        }
                        // Reset the deferred prompt variable
                      }
                    );
                  } else {
                    setIsHomeScreenOpen(true);
                  }
                }}
                className='!text-16 justify-left vw:min-h-[30px]  mx-auto mt-auto w-[95%] !rounded-lg !bg-[#0000000f] pb-2 !text-left !text-black'
              >
                <svg
                  className='mr-[10px]'
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M12.6666 6C12.6666 6.36819 12.9651 6.66667 13.3333 6.66667C13.7014 6.66667 13.9999 6.36819 13.9999 6V3.33333C13.9999 2.59695 13.403 2 12.6666 2H9.99992C9.63173 2 9.33325 2.29848 9.33325 2.66667C9.33325 3.03486 9.63173 3.33333 9.99992 3.33333L11.7238 3.33333L7.52851 7.5286C7.26816 7.78895 7.26816 8.21106 7.52851 8.4714C7.78886 8.73175 8.21097 8.73175 8.47132 8.4714L12.6666 4.27614V6Z'
                    fill='black'
                    fill-opacity='0.7'
                  />
                  <path
                    fill-rule='evenodd'
                    clip-rule='evenodd'
                    d='M2 4C2 2.89543 2.89543 2 4 2H7.33333C7.70152 2 8 2.29848 8 2.66667C8 3.03486 7.70152 3.33333 7.33333 3.33333H4C3.63181 3.33333 3.33333 3.63181 3.33333 4V12C3.33333 12.3682 3.63181 12.6667 4 12.6667H12C12.3682 12.6667 12.6667 12.3682 12.6667 12V8.66667C12.6667 8.29848 12.9651 8 13.3333 8C13.7015 8 14 8.29848 14 8.66667V12C14 13.1046 13.1046 14 12 14H4C2.89543 14 2 13.1046 2 12V4Z'
                    fill='black'
                    fill-opacity='0.7'
                  />
                </svg>
                <span className='vw:text-16 font-bold'>
                  {' '}
                  Add to home screen
                </span>
              </ButtonCore>
            </div>
          </div>
        )}
      </Drawer>
      <Modal
        footer={null}
        open={isHomeScreenOpen}
        title='Add to home screen'
        closable
        onCancel={() => setIsHomeScreenOpen(false)}
      >
        <div>
          <div className='relative my-6'>
            <span className='text-14 absolute -top-4 left-3 rounded-2xl bg-[#CF1322] px-3 py-1 text-white'>
              Step 1
            </span>
            <img
              className='h-[65px] w-[397px]'
              src='/img/header/instruction_1.png'
              alt=''
            />
          </div>
          <div className='relative'>
            <span className='text-14 absolute -top-4 left-3 rounded-2xl bg-[#CF1322] px-3 py-1 text-white'>
              Step 2
            </span>
            <img
              className='h-[230px] w-[397px]'
              src='/img/header/instruction_2.png'
              alt=''
            />
          </div>

          <ButtonCore
            onClick={() => setIsHomeScreenOpen(false)}
            className='mx-auto my-6 mb-3 !px-12'
          >
            Got It
          </ButtonCore>
        </div>
      </Modal>
    </header>
  );
};

export default MobileHeader;
